import React, { useMemo } from 'react';
import { Box, CardTypesEnum, PlanCardChips, RadioButton, Text } from '@common-fe/common-fe';
import styled from 'styled-components';

import { ExpressSetup } from '@/modules/ExpressSetup/ExpressSetup.types.ts';
import { Plan } from '@/modules/plan/plan.types.ts';

import PlanCode from './PlanCode';

interface StyledBoxProps {
  isChecked?: boolean;
  isDisabled?: boolean;
}

const StyledBox = styled(Box)<StyledBoxProps>`
  border: ${({ isChecked, theme }) => isChecked ? `2px solid ${theme.colors.border1}` : '2px solid transparent'};
  box-shadow: ${({ isChecked, theme }) => {
    if (!isChecked) {
      return `0 0 0 1px ${theme.colors.border1}`;
    }
    return 'none';
  }};
  ${({ theme, isDisabled }) => !isDisabled && `
    &:hover {
      border: 2px solid ${theme.colors.border1};
      box-shadow: none;
    }
  `}
  cursor: ${({ isDisabled }) => isDisabled ? 'not-allowed' : 'cursor'};
`;

export const PLAN_TYPE_CARDS = [
  { key: CardTypesEnum.HSA, title: 'Health Savings Account', description: '' },
  { key: CardTypesEnum.HCFSA, title: 'Health Care Flexible Spending Account', description: '' },
  { key: CardTypesEnum.HRA, title: 'Health Reimbursement Arrangement', description: '' },
  { key: CardTypesEnum.DCAP, title: 'Dependent Care Assistance Program', description: 'Dependent care FSAs allow employees to pay for child or elder care costs with pre-tax funds. These accounts have usage deadlines and do not rollover.' },
  { key: CardTypesEnum.PARKING, title: 'Parking', description: 'Commuter accounts allow employees to pay for their work commutes—transit and parking expenses—with pre-tax funds.' },
  { key: CardTypesEnum.TRANSIT, title: 'Transit', description: 'Commuter accounts allow employees to pay for their work commutes—transit and parking expenses—with pre-tax funds.' },
  { key: CardTypesEnum.ADOPTION, title: 'Adoption', description: '' },
  { key: CardTypesEnum.LIFESTYLE, title: 'Lifestyle', description: '' },
  { key: CardTypesEnum.SPECIALTY, title: 'Specialty', description: '' },
  { key: CardTypesEnum.TRAVEL, title: 'Travel Reimbursement', description: '' },
];

export interface Props {
  plan: Plan;
  isLastItem: boolean;
  isChecked?: boolean;
  isDisabled?: boolean;
  expressSetupData?: ExpressSetup;
  isPlansErrorShows?: boolean;
  onClick: () => void;
}

const PlanRadioOptionCard: React.FC<Props> = ({
  plan,
  isLastItem,
  isChecked,
  isDisabled,
  expressSetupData,
  isPlansErrorShows,
  onClick,
}) => {
  const shouldDisplayChips = useMemo(() => {
    return plan?.isRolloverChipsDisplay || plan?.isGracePeriodChipsDisplay || plan?.isRunOutChipsDisplay || plan?.isAllowInvestmentsChipsDisplay;
  }, [plan]);
  return (
    <StyledBox
      direction="row"
      data-testid="plan-radio-option-card"
      background="canvas"
      pad={{ vertical: 'm', horizontal: 'l' }}
      round="container1Round"
      margin={{ bottom: isLastItem ? 'none' : 'xs' }}
      height="fit-content"
      justify="between"
      onClick={!isDisabled ? onClick : undefined}
      isChecked={isChecked}
      isDisabled={isDisabled}
    >
      <Box width={{ min: '32px' }} margin={{ right: 's' }} align="center" justify="center">
        <RadioButton
          name={plan?.id}
          disabled={isDisabled}
          onChange={onClick}
          checked={isChecked}
        />
      </Box>
      <Box width="100%" gap="xxxs">
        <Text weight={700} size="16px" style={{ lineHeight: '22px'}} color="textTitle">
          {plan?.name || ''}
        </Text>
        {
          shouldDisplayChips && (
            <PlanCardChips 
              isRolloverChipsDisplay={plan?.isRolloverChipsDisplay}
              isGracePeriodChipsDisplay={plan?.isGracePeriodChipsDisplay}
              isRunOutChipsDisplay={plan?.isRunOutChipsDisplay}
              isAllowInvestmentsChipsDisplay={plan?.isAllowInvestmentsChipsDisplay}
              maxRolloverAmount={plan?.maxRolloverAmount}
              maxRolloverAmountType={plan?.maxRolloverAmountType}
              rolloverClaimsType={plan?.rolloverClaimsType}
            />
          )
        }
        {
          plan?.summary && (
            <Text color="textSecondary">
              {plan?.summary || ''}
            </Text>
          )
        }
        {
          isChecked && (
            <PlanCode
              planTemplate={plan}
              savedPlans={expressSetupData?.plans}
              showError={isPlansErrorShows}
            />
          )
        }
      </Box>
    </StyledBox>
  );
};

export default PlanRadioOptionCard;
